import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Layout from "@/components/feature/Layout";
import { articles, categories } from "@/mocks/data";
import JsonLd from "@/components/base/JsonLd";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const categoryInfo = useMemo(
    () => categories.find((c) => c.slug === article?.categorySlug),
    [article]
  );

  const articleSchema = article ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    image: article.imageUrl,
    articleBody: article.content.replace(/<[^>]*>/g, "").substring(0, 500),
    publisher: {
      "@type": "Organization",
      name: "妈妈的朋友2 影迷资讯站",
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://showpath4.github.io/article/${article.slug}`,
    },
  } : null;

  const breadcrumbSchema = article ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://showpath4.github.io/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: article.category,
        item: `https://showpath4.github.io/category/${article.categorySlug}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  } : null;

  if (!article) {
    return (
      <Layout>
        <div className="w-full pt-32 pb-20 px-4 text-center">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">
            文章未找到
          </h1>
          <p className="text-foreground-600 mb-8">你访问的文章可能已被移除或链接有误</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-all cursor-pointer"
          >
            <i className="ri-home-line"></i>
            返回首页
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {articleSchema && <JsonLd data={articleSchema} />}
      {breadcrumbSchema && <JsonLd data={breadcrumbSchema} />}
      <article className="w-full">
        <div className="w-full pt-24 pb-8 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
                {article.category}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs text-foreground-500 mb-8">
              <span className="flex items-center gap-1.5">
                <div className="w-6 h-6 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600 text-xs"></i>
                </div>
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                阅读 {article.readTime}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views >= 10000 ? `${(article.views / 10000).toFixed(1)}万` : article.views} 次阅读
              </span>
              <span className="flex items-center gap-1 text-accent-600">
                <i className="ri-star-fill"></i>
                {article.rating} 分
              </span>
            </div>
          </div>
        </div>

        <div className="relative w-full h-64 md:h-96 overflow-hidden">
          <img
            src={article.imageUrl.replace("width=800&height=450", "width=1600&height=900")}
            alt={article.title}
            className="w-full h-full object-cover object-top"
            title={article.title}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-100 via-transparent to-transparent"></div>
        </div>

        <div className="w-full py-10 md:py-14 px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl md:[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:mb-5 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </div>

        {relatedArticles.length > 0 && (
          <div className="w-full py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-40 overflow-hidden">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        title={rel.title}
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span>{rel.date}</span>
                        <span>{rel.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </article>
    </Layout>
  );
}